import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, Crown } from 'lucide-react';
import { questsApi } from '../../api';
import { useAuth } from '../../context/AuthContext';

const Leaderboard = ({ communityId = null, limit = 10 }) => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await questsApi.getLeaderboard(communityId, limit);
        setLeaderboard(response.data);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaderboard();
  }, [communityId, limit]);

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1: return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2: return <Medal className="w-5 h-5 text-gray-400" />;
      case 3: return <Award className="w-5 h-5 text-orange-400" />;
      default: return <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-gray-500">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1: return 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200';
      case 2: return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
      case 3: return 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200';
      default: return 'bg-white border-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  if (!leaderboard?.entries?.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="text-center py-8 text-gray-500">
          <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No leaderboard data yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="font-semibold text-gray-900">Leaderboard</h3>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {communityId ? 'Community rankings' : 'Global rankings'}
        </p>
      </div>

      <div className="divide-y divide-gray-100">
        {leaderboard.entries.map((entry) => (
          <div
            key={entry.user_id}
            className={`flex items-center gap-4 p-4 transition-colors hover:bg-gray-50 ${
              entry.user_id === user?.id ? 'bg-emerald-50' : ''
            } ${getRankBg(entry.rank)} border-l-4 ${entry.rank <= 3 ? '' : 'border-transparent'}`}
          >
            <div className="w-8 flex justify-center">
              {getRankIcon(entry.rank)}
            </div>
            
            <img
              src={entry.user_avatar || `https://ui-avatars.com/api/?name=${entry.user_name}&background=10b981&color=fff`}
              alt={entry.user_name}
              className="w-10 h-10 rounded-full"
            />
            
            <div className="flex-1 min-w-0">
              <p className={`font-medium truncate ${
                entry.user_id === user?.id ? 'text-emerald-700' : 'text-gray-900'
              }`}>
                {entry.user_name}
                {entry.user_id === user?.id && ' (You)'}
              </p>
              <p className="text-sm text-gray-500">
                Level {entry.level} • {entry.quests_completed} quests
              </p>
            </div>
            
            <div className="text-right">
              <p className="font-semibold text-gray-900">{entry.total_xp.toLocaleString()}</p>
              <p className="text-xs text-gray-500">XP</p>
            </div>
            
            <div className="flex items-center gap-1">
              <Trophy className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-gray-600">{entry.achievements_count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Leaderboard;
